import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { sendBondCalculation } from '../http/api';
import { useBondCalculationStore } from '../Store/bondCalculationStore';

// Reusable helper: return current UTC date at 00:00:00
const getCurrentUTCDate = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

// New helper: return current UTC date as YYYY-MM-DD string
const getCurrentUTCDateString = () => {
  return getCurrentUTCDate().toISOString().split('T')[0];
};

const isUsablePrice = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized || normalized === 'null' || normalized === 'undefined') return false;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

const useBondCalculations = ({ cusip: initialCusip, price: initialPrice, userId, token, onSettlementDateReceived }) => {
  const [pendingCalcUUIDs, setPendingCalcUUIDs] = useState([]);
  const [bondCalcResults, setBondCalcResults] = useState(null);
  const [calcErrorMessage, setCalcErrorMessage] = useState(null);
  // Remove apiCallInProgress state
  // const [apiCallInProgress, setApiCallInProgress] = useState(false);
  
  // Track processed UUIDs to prevent duplicate processing
  const processedUUIDsRef = useRef(new Set());
  
  // Get store methods
  const { 
    calculateBondValues: storeCalculateBondValues,
    removePendingRequest,
    storeCalculationResult
  } = useBondCalculationStore();

  // Loader is true if there are any pending UUIDs
  const calcLoading = useMemo(() => pendingCalcUUIDs.length > 0, [pendingCalcUUIDs]);


  const calculateBondValues = useCallback(async (price, cusip, tradeDate = getCurrentUTCDate()) => {
    const cusipToUse = cusip || initialCusip;
    const priceToUse = price || initialPrice;
    // normalize tradeDate to a UTC YYYY-MM-DD string to avoid local-timezone display confusion
    const tradeDateToUseStr = (() => {
      if (!tradeDate) return getCurrentUTCDateString();

      // If it's already a Date, use its UTC date portion
      if (tradeDate instanceof Date) {
        return tradeDate.toISOString().split('T')[0];
      }

      // If it's a string (e.g. "2025-09-25T08:38:00"), try parsing to Date and normalize to UTC YYYY-MM-DD
      if (typeof tradeDate === 'string') {
        const parsed = new Date(tradeDate);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0];
        }
        // Fallback: take first 10 characters which usually represent YYYY-MM-DD
        return tradeDate.slice(0, 10);
      }

      // Fallback for other types
      try {
        const parsedOther = new Date(String(tradeDate));
        if (!isNaN(parsedOther.getTime())) return parsedOther.toISOString().split('T')[0];
      } catch (e) {
        // ignore and fallback
      }

      return getCurrentUTCDateString();
    })();

    console.log('Calculating bond values for:', { cusip: cusipToUse, price: priceToUse, tradeDate: tradeDateToUseStr });

    if (!cusipToUse || cusipToUse.length < 9) {
      console.log('Skipping calculation - invalid CUSIP:', cusipToUse);
      return;
    }

    if (!isUsablePrice(priceToUse)) {
      console.log('Skipping calculation - invalid price:', priceToUse);
      return;
    }

    const uuid = uuidv4(); // Move UUID declaration outside try block
    
    try {
      setCalcErrorMessage(null);
      setPendingCalcUUIDs(prev => [...prev, uuid]);
      // Remove setApiCallInProgress(true);

      const calcData = {
        cusip: cusipToUse,
        price: Number(priceToUse),
        userId,
        UUID: uuid,
        tradeDate: tradeDateToUseStr // already YYYY-MM-DD (UTC)
      };
      //removed useless code
      const result = await sendBondCalculation(token, calcData);
      
      // Remove setApiCallInProgress(false);

      if (result.error) {
        setCalcErrorMessage(result.message || "Failed to calculate bond values");
        // Remove UUID from pending when API returns error
        setPendingCalcUUIDs(prev => prev.filter(id => id !== uuid));
      }

      return uuid; // Return the UUID so caller can track it
    } catch (error) {
      console.error("Error sending bond calculation:", error);
      setCalcErrorMessage(error.message || "Failed to send bond calculation request");
      // Remove setApiCallInProgress(false);
      // Remove UUID from pending when API call fails
      setPendingCalcUUIDs(prev => prev.filter(id => id !== uuid));
    }
  }, [userId, token, initialCusip, initialPrice]);

  useEffect(() => {
    const removePending = (uuid) => {
      setPendingCalcUUIDs(prev => prev.filter(id => id !== uuid));
    };

    const handleBondCalcUpdate = (event) => {
      const calcData = event.detail;
      
      // Implement deduplication - only process each UUID once
      if (processedUUIDsRef.current.has(calcData.UUID)) {
        console.log(`Duplicate bond calc update for UUID ${calcData.UUID} - ignoring`);
        return;
      }
      
      // Mark this UUID as processed
      processedUUIDsRef.current.add(calcData.UUID);
      
      if (pendingCalcUUIDs.includes(calcData.UUID)) {
        removePending(calcData.UUID);
        setCalcErrorMessage(null);
        setBondCalcResults(calcData);
        
        // Handle settlement date if present
        if (calcData.Settlement_Date && onSettlementDateReceived) {
          // Convert from "2025/09/29" to "2025-09-29" format
          const formattedDate = calcData.Settlement_Date.replace(/\//g, '-');
          onSettlementDateReceived(formattedDate);
        }
        
        // Store the result in our central store
        storeCalculationResult(calcData.UUID, calcData);
      }
    };

    const handleBondCalcError = (event) => {
      const errorData = event.detail;
      
      // Implement deduplication for errors too
      if (processedUUIDsRef.current.has(errorData.UUID)) {
        console.log(`Duplicate bond calc error for UUID ${errorData.UUID} - ignoring`);
        return;
      }
      
      // Mark this UUID as processed
      processedUUIDsRef.current.add(errorData.UUID);
      
      if (pendingCalcUUIDs.includes(errorData.UUID)) {
        removePending(errorData.UUID);
        setBondCalcResults(null);
        setCalcErrorMessage(errorData.message || "Error calculating bond values");
        
        // Remove from pending in the store
        removePendingRequest(errorData.UUID);
      }
    };

    // Clean up the processed UUIDs set when the pending UUIDs change
    // This prevents the set from growing indefinitely
    const cleanup = () => {
      if (pendingCalcUUIDs.length === 0) {
        // Only keep processed UUIDs that are still pending
        const newProcessedUUIDs = new Set();
        pendingCalcUUIDs.forEach(uuid => {
          if (processedUUIDsRef.current.has(uuid)) {
            newProcessedUUIDs.add(uuid);
          }
        });
        processedUUIDsRef.current = newProcessedUUIDs;
      }
    };

    window.addEventListener("bondCalcUpdate", handleBondCalcUpdate);
    window.addEventListener("bondCalcError", handleBondCalcError);

    // Clean up whenever pendingCalcUUIDs changes
    cleanup();

    return () => {
      window.removeEventListener("bondCalcUpdate", handleBondCalcUpdate);
      window.removeEventListener("bondCalcError", handleBondCalcError);
    };
  }, [pendingCalcUUIDs, storeCalculationResult, removePendingRequest, onSettlementDateReceived]);

  return {
    setBondCalcResults,
    bondCalcResults,
    calcLoading,
    calcErrorMessage,
    calculateBondValues,
    pendingCalcUUIDs // Expose the pending UUIDs
  };
};

export default useBondCalculations;
